/**
 * Shared column definitions for CompactTable used across reporting tabs
 * (Client Reporting, Carrier Reporting, etc.)
 */
import { Box, Chip, Link, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { type CompactTableColumn } from "@/components/common/CompactTable";
import { StatusChip } from "@/components/common/fields/StatusChipField";
import { getStatusColor } from "@/utils/helpers/getStatusColor";
import { ColorType, UIComponentSize } from "@/types/shared/uiComponents";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { formatShortDate } from "@/utils/formatters/dateFormatter";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import { Reimbursement, StoplossClaim } from "@/types";

// ─── Stoploss Claim Columns ────────────────────────────────────────────────

export const stoplossClaimColumns: CompactTableColumn<StoplossClaim & { claimant_name?: string }>[] = [
  {
    key: "claimant",
    label: "Claimant",
    width: "1fr",
    render: (claim) =>
      claim.claimant_id_ref ? (
        <Link
          component={RouterLink}
          to={`/claimants/${claim.claimant_id_ref}/show`}
          underline="always"
          onClick={(e) => e.stopPropagation()}
          sx={{ fontWeight: 500, fontSize: "0.875rem" }}
          noWrap
        >
          {claim.claimant_name || "—"}
        </Link>
      ) : (
        <Typography variant="body2" color="text.secondary">
          —
        </Typography>
      ),
  },
  {
    key: "type",
    label: "Type",
    width: "70px",
    render: (claim) => (
      <Chip
        label={formatToTitleCase(claim.stoploss_claim_type || "—")}
        size="small"
        variant="outlined"
        sx={{ height: 22, fontSize: "0.7rem" }}
      />
    ),
  },
  {
    key: "total_paid",
    label: "Total Paid",
    width: "100px",
    align: "right",
    render: (claim) => (
      <Typography variant="body2" fontWeight={500}>
        {claim.total_paid_amount ? formatUSD(claim.total_paid_amount) : "—"}
      </Typography>
    ),
  },
  {
    key: "last_paid",
    label: "Last Paid",
    width: "80px",
    align: "right",
    render: (claim) => (
      <Typography variant="caption" color="text.secondary">
        {formatShortDate(claim.last_paid_date)}
      </Typography>
    ),
  },
  {
    key: "status",
    label: "Status",
    width: "90px",
    align: "right",
    render: (claim) => (
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <StatusChip
          label={formatToTitleCase(claim.claim_status)}
          color={getStatusColor(claim.claim_status) ?? ColorType.Default}
          size={UIComponentSize.Small}
        />
      </Box>
    ),
  },
];

// ─── Reimbursement Columns ──────────────────────────────────────────────────

export const reimbursementColumns: CompactTableColumn<Reimbursement & { claimant_name?: string }>[] = [
  {
    key: "claimant",
    label: "Claimant",
    width: "1fr",
    render: (reimb) =>
      reimb.claimant_id_ref ? (
        <Link
          component={RouterLink}
          to={`/claimants/${reimb.claimant_id_ref}/show`}
          underline="always"
          onClick={(e) => e.stopPropagation()}
          sx={{ fontWeight: 500, fontSize: "0.875rem" }}
          noWrap
        >
          {reimb.claimant_name || "—"}
        </Link>
      ) : (
        <Typography variant="body2" color="text.secondary">
          —
        </Typography>
      ),
  },
  {
    key: "requested",
    label: "Requested",
    width: "100px",
    align: "right",
    render: (reimb) => (
      <Typography variant="body2" fontWeight={500}>
        {reimb.requested_amount ? formatUSD(reimb.requested_amount) : "—"}
      </Typography>
    ),
  },
  {
    key: "reimbursed",
    label: "Reimbursed",
    width: "100px",
    align: "right",
    render: (reimb) => {
      const requested = Number(reimb.requested_amount) || 0;
      const reimbursed = Number(reimb.reimbursement_amount) || 0;
      const color = reimbursed <= 0 ? "text.secondary" : reimbursed >= requested ? "success.main" : "warning.main";
      return (
        <Typography variant="body2" color={color}>
          {reimbursed ? formatUSD(reimbursed) : "—"}
        </Typography>
      );
    },
  },
  {
    key: "status",
    label: "Status",
    width: "90px",
    align: "right",
    render: (reimb) => (
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <StatusChip
          label={formatToTitleCase(reimb.status)}
          color={getStatusColor(reimb.status) ?? ColorType.Default}
          size={UIComponentSize.Small}
        />
      </Box>
    ),
  },
];
